import { c as _c } from "react/compiler-runtime";
import { useMergedRef } from '@mantine/hooks';
import React, { useMemo, useState, useEffect } from 'react';
import { useElementSize } from 'shared/ui';
import { twMerge } from 'tailwind-merge';
import { isChrome } from 'react-device-detect';
import { match } from 'ts-pattern';
const useHydrated = () => {
  const $ = _c(2);
  const [hydrated, setHydrated] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => setHydrated(true);
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return hydrated;
};
export type GlassProps = {
  children?: React.ReactNode;
  className?: string;
  frost?: number;
  showBorder?: boolean;
  displace?: number;
  mode?: 'svg-displacement' | 'blur';
  disableAnimation?: boolean;
  disabled?: boolean;
  classNames?: {
    root?: string;
    content?: string;
  };
  ref?: React.RefObject<HTMLElement | null>;
};

/**
 * A div with a fancy glass look
 * A react wrapper over https://codepen.io/jh3y/pen/EajLxJV
 */
export const Glass = t0 => {
  const $ = _c(41);
  let children;
  let className;
  let classNames;
  let disableAnimation;
  let displace;
  let frost;
  let modeRaw;
  let props;
  let ref;
  let showBorder;
  let style;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      className,
      frost,
      showBorder,
      displace,
      disableAnimation,
      disabled: t1,
      classNames,
      style,
      ref,
      mode: modeRaw,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = classNames;
    $[4] = disableAnimation;
    $[5] = displace;
    $[6] = frost;
    $[7] = modeRaw;
    $[8] = props;
    $[9] = ref;
    $[10] = showBorder;
    $[11] = style;
    $[12] = t1;
  } else {
    children = $[1];
    className = $[2];
    classNames = $[3];
    disableAnimation = $[4];
    displace = $[5];
    frost = $[6];
    modeRaw = $[7];
    props = $[8];
    ref = $[9];
    showBorder = $[10];
    style = $[11];
    t1 = $[12];
  }
  const disabled = t1 === undefined ? false : t1;
  const hydrated = useHydrated();
  const mode = modeRaw ?? (hydrated && isChrome ? "svg-displacement" : "blur");
  const {
    ref: sizeRef,
    height,
    width
  } = useElementSize();
  const t2 = displace ?? 2;
  const t3 = frost ?? 0.6;
  const t4 = showBorder ?? true;
  let t5;
  if ($[13] !== height || $[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== width) {
    t5 = {
      width,
      height,
      radius: 1,
      border: -0.1,
      lightness: 50,
      blend: "difference" as const,
      alpha: 0.93,
      blur: 5,
      x: "R" as const,
      y: "B" as const,
      r: 0,
      g: 8,
      b: 12,
      scale: -180,
      displace: t2,
      frost: t3,
      showBorder: t4
    };
    $[13] = height;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = width;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const config = t5;
  let t6;
  bb0: {
    if (config.width === 0 || config.height === 0) {
      t6 = "";
      break bb0;
    }
    const border = Math.min(config.width, config.height) * (config.border * 0.5);
    const svg = `
      <svg viewBox="0 0 ${config.width} ${config.height}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="red" x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" stop-color="#0000"/>
            <stop offset="100%" stop-color="red"/>
          </linearGradient>
          <linearGradient id="blue" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#0000"/>
            <stop offset="100%" stop-color="blue"/>
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="${config.width}" height="${config.height}" fill="black"></rect>
        <rect x="0" y="0" width="${config.width}" height="${config.height}" rx="${config.radius}" fill="url(#red)" />
        <rect x="0" y="0" width="${config.width}" height="${config.height}" rx="${config.radius}" fill="url(#blue)" style="mix-blend-mode: ${config.blend}" />
        <rect x="${border}" y="${border}" width="${config.width - border * 2}" height="${config.height - border * 2}" rx="${config.radius}" fill="hsl(0 0% ${config.lightness}% / ${config.alpha})" style="filter:blur(${config.blur}px)" />
      </svg>
    `;
    t6 = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }
  const displacementImage = t6;
  const mergedRef = useMergedRef(sizeRef, ref);
  let t7;
  if ($[19] !== children || $[20] !== className || $[21] !== classNames?.content || $[22] !== classNames?.root || $[23] !== config.b || $[24] !== config.displace || $[25] !== config.frost || $[26] !== config.g || $[27] !== config.r || $[28] !== config.scale || $[29] !== config.showBorder || $[30] !== config.x || $[31] !== config.y || $[32] !== disableAnimation || $[33] !== disabled || $[34] !== displacementImage || $[35] !== mergedRef || $[36] !== mode || $[37] !== props || $[38] !== showBorder || $[39] !== style) {
    t7 = match({
      mode,
      disabled
    }).with({
      mode: "svg-displacement",
      disabled: false
    }, () => <div ref={mergedRef} {...props} className={twMerge("rounded-[12px]", !disableAnimation && "transition-all duration-200 ease-in-out", !disabled && "[backdrop-filter:url(#glass-filter)]", !disabled && config.showBorder && "shadow-[inset_0_0_2px_1px_rgba(0,0,0,0.15),inset_0_0_10px_4px_rgba(0,0,0,0.1),0_4px_16px_rgba(17,17,26,0.05),0_8px_24px_rgba(17,17,26,0.05),0_16px_56px_rgba(17,17,26,0.05),inset_0_4px_16px_rgba(17,17,26,0.05),inset_0_8px_24px_rgba(17,17,26,0.05),inset_0_16px_56px_rgba(17,17,26,0.05)]", !disabled && config.showBorder && "dark:shadow-[inset_0_0_2px_1px_rgba(255,255,255,0.35),inset_0_0_10px_4px_rgba(255,255,255,0.15),0_4px_16px_rgba(17,17,26,0.05),0_8px_24px_rgba(17,17,26,0.05),0_16px_56px_rgba(17,17,26,0.05),inset_0_4px_16px_rgba(17,17,26,0.05),inset_0_8px_24px_rgba(17,17,26,0.05),inset_0_16px_56px_rgba(17,17,26,0.05)]", className, classNames?.root)} style={{
      "--frost-opacity": disabled ? 0 : config.frost,
      backgroundColor: disabled ? "transparent" : `color-mix(in oklch, var(--mantine-color-body) ${config.frost * 100}%, transparent)`,
      ...style
    } as React.CSSProperties}><div className={twMerge(classNames?.content)}>{children}</div><svg className={twMerge("pointer-events-none absolute inset-0 h-full w-full", disabled && "opacity-0")}><defs><filter id="glass-filter" colorInterpolationFilters="sRGB"><feImage x="0" y="0" width="100%" height="100%" result="map" href={displacementImage} /><feDisplacementMap in="SourceGraphic" in2="map" xChannelSelector={config.x} yChannelSelector={config.y} result="dispRed" scale={config.scale + config.r} /><feColorMatrix in="dispRed" type="matrix" values={"1 0 0 0 0\n                    0 0 0 0 0\n                    0 0 0 0 0\n                    0 0 0 1 0"} result="red" /><feDisplacementMap in="SourceGraphic" in2="map" xChannelSelector={config.x} yChannelSelector={config.y} result="dispGreen" scale={config.scale + config.g} /><feColorMatrix in="dispGreen" type="matrix" values={"0 0 0 0 0\n                    0 1 0 0 0\n                    0 0 0 0 0\n                    0 0 0 1 0"} result="green" /><feDisplacementMap in="SourceGraphic" in2="map" xChannelSelector={config.x} yChannelSelector={config.y} result="dispBlue" scale={config.scale + config.b} /><feColorMatrix in="dispBlue" type="matrix" values={"0 0 0 0 0\n                    0 0 0 0 0\n                    0 0 1 0 0\n                    0 0 0 1 0"} result="blue" /><feBlend in="red" in2="green" mode="screen" result="rg" /><feBlend in="rg" in2="blue" mode="screen" result="output" /><feGaussianBlur in="output" stdDeviation={config.displace} /></filter></defs></svg></div>).otherwise(() => <div ref={mergedRef} {...props} style={{
      ...(showBorder ? {} : {
        boxShadow: "none"
      }),
      ...style
    }} className={twMerge(!disabled && mode == "blur" && "glass rounded-lg", showBorder ? "" : "shadow-none", className, classNames?.root, classNames?.content)}>{children}</div>);
    $[19] = children;
    $[20] = className;
    $[21] = classNames?.content;
    $[22] = classNames?.root;
    $[23] = config.b;
    $[24] = config.displace;
    $[25] = config.frost;
    $[26] = config.g;
    $[27] = config.r;
    $[28] = config.scale;
    $[29] = config.showBorder;
    $[30] = config.x;
    $[31] = config.y;
    $[32] = disableAnimation;
    $[33] = disabled;
    $[34] = displacementImage;
    $[35] = mergedRef;
    $[36] = mode;
    $[37] = props;
    $[38] = showBorder;
    $[39] = style;
    $[40] = t7;
  } else {
    t7 = $[40];
  }
  return t7;
};